import { ConnectionQuality, ConnectionState, DisconnectReason } from 'livekit-client';

/**
 * Накопитель телеметрии качества для одного захода в комнату — общий для 1:1 звонков и войса
 * сборов.
 *
 * Всё, что он считает, LiveKit уже сообщает клиенту, а мы до сих пор выбрасывали: связь рвалась,
 * человек переподключался, браузер заблокировал звук, микрофон не отдал ни слова. Это видно по
 * 100 % сеансов и не стоит человеку ни одного тапа — в отличие от эха и «слышно плохо при зелёных
 * метриках», которые ловятся только опросом.
 *
 * Считает ВРЕМЯ, а не события: «связь была плохой 4 минуты из 6» — это ответ, а «качество менялось
 * 11 раз» — нет.
 */
export type QualitySnapshot = {
  durationSeconds: number;
  poorSeconds: number;
  lostSeconds: number;
  reconnects: number;
  playbackBlocked: boolean;
  spoke: boolean;
  micDenied: boolean;
  disconnectReason: string | null;
};

const now = () => Date.now();

const toSeconds = (ms: number) => Math.max(0, Math.round(ms / 1000));

/**
 * Коды причин разрыва короткие и стабильные: колонка в базе 32 символа, а числовой enum SDK
 * при обновлении версии может поехать значениями.
 */
const DISCONNECT_REASONS: Partial<Record<DisconnectReason, string>> = {
  [DisconnectReason.CLIENT_INITIATED]: 'client',
  [DisconnectReason.DUPLICATE_IDENTITY]: 'other_tab',
  [DisconnectReason.SERVER_SHUTDOWN]: 'server_shutdown',
  [DisconnectReason.PARTICIPANT_REMOVED]: 'removed',
  [DisconnectReason.ROOM_DELETED]: 'room_closed',
  [DisconnectReason.STATE_MISMATCH]: 'state_mismatch',
  [DisconnectReason.JOIN_FAILURE]: 'join_failure',
};

export type QualityTracker = {
  /** Смена качества связи локального участника. */
  onQuality: (quality: ConnectionQuality) => void;
  /** Смена состояния соединения — отсюда берутся переподключения. */
  onConnectionState: (state: ConnectionState) => void;
  /** Браузер заблокировал воспроизведение: человек не слышит НИЧЕГО и не понимает почему. */
  onPlaybackBlocked: () => void;
  /** Человек оказался активным говорящим хотя бы раз. */
  onSpoke: () => void;
  /**
   * Микрофон не отдали: отказ в системном диалоге, запрет в настройках сайта, отобранное
   * приложению разрешение. Без этого флага такой человек неотличим от того, кто просто молчал —
   * оба приезжают как `spoke: false`, и «у нас 40% молчунов» ничего не говорит о том, сломано ли
   * что-нибудь.
   */
  onMicDenied: () => void;
  onDisconnected: (reason?: DisconnectReason) => void;
  /**
   * Снять накопленное и обнулить счётчики. Обнуление здесь не деталь, а защита: снимок берут при
   * выходе И на выгрузке страницы, и без сброса один заход отправился бы дважды.
   */
  take: () => QualitySnapshot;
  /**
   * Начать отсчёт заново. Вызывается в момент подключения: хук живёт всё время, пока открыта
   * страница, и без сброса в длительность сеанса попало бы всё время до входа в комнату.
   */
  reset: () => void;
  /**
   * Есть ли что отправлять. Три секунды, а не одна: эффект, владеющий комнатой, пересоздаётся на
   * смене фазы соединения, и без порога каждый такой пересбор слал бы сводку-пустышку.
   */
  hasData: () => boolean;
};

export const createQualityTracker = (): QualityTracker => {
  let startedAt = now();
  // Текущее состояние качества и момент, когда оно наступило: время в Poor/Lost считается
  // отрезками между сменами, иначе последний отрезок до выхода потерялся бы целиком.
  let currentQuality: ConnectionQuality | null = null;
  // Последнее, что сообщил SDK, отдельно от «принудительного Lost» на время переподключения.
  // ConnectionQualityChanged приходит только на СМЕНУ качества: если связь была плохой до
  // разрыва и осталась плохой после, второго события не будет. Без этой памяти самые скверные
  // сеансы после первого же реконнекта переставали бы копить poorSeconds и уезжали в отчёт
  // здоровыми.
  let reportedQuality: ConnectionQuality | null = null;
  let qualitySince = now();

  let poorMs = 0;
  let lostMs = 0;
  let reconnects = 0;
  let playbackBlocked = false;
  let spoke = false;
  let micDenied = false;
  let disconnectReason: string | null = null;

  const closeCurrentSpan = () => {
    const elapsed = now() - qualitySince;
    if (currentQuality === ConnectionQuality.Poor) {
      poorMs += elapsed;
    } else if (currentQuality === ConnectionQuality.Lost) {
      lostMs += elapsed;
    }
    qualitySince = now();
  };

  return {
    onQuality: (quality) => {
      closeCurrentSpan();
      currentQuality = quality;
      reportedQuality = quality;
    },
    onConnectionState: (state) => {
      if (state === ConnectionState.Reconnecting) {
        reconnects += 1;
        // Переподключение — это уже потерянная связь, даже если ConnectionQuality не успел
        // доехать до Lost: SDK рвёт транспорт быстрее, чем присылает деления.
        closeCurrentSpan();
        currentQuality = ConnectionQuality.Lost;
      }
      if (state === ConnectionState.Connected && currentQuality === ConnectionQuality.Lost) {
        closeCurrentSpan();
        // Возвращаемся к тому, что SDK сообщал в последний раз, а не к «неизвестно».
        currentQuality = reportedQuality === ConnectionQuality.Lost ? null : reportedQuality;
      }
    },
    onPlaybackBlocked: () => {
      playbackBlocked = true;
    },
    onSpoke: () => {
      spoke = true;
    },
    onMicDenied: () => {
      micDenied = true;
    },
    onDisconnected: (reason) => {
      closeCurrentSpan();
      currentQuality = null;
      // Первая причина побеждает. Наш собственный teardown отмечается последним и не должен
      // затирать настоящую причину разрыва — иначе каждый обрыв выглядел бы как «положил трубку».
      if (reason !== undefined && disconnectReason === null) {
        disconnectReason = DISCONNECT_REASONS[reason] ?? `code_${reason}`;
      }
    },
    reset: () => {
      startedAt = now();
      qualitySince = now();
      currentQuality = null;
      reportedQuality = null;
      poorMs = 0;
      lostMs = 0;
      reconnects = 0;
      playbackBlocked = false;
      spoke = false;
      micDenied = false;
      disconnectReason = null;
    },
    take: () => {
      closeCurrentSpan();
      const snapshot: QualitySnapshot = {
        durationSeconds: toSeconds(now() - startedAt),
        poorSeconds: toSeconds(poorMs),
        lostSeconds: toSeconds(lostMs),
        reconnects,
        playbackBlocked,
        spoke,
        micDenied,
        disconnectReason,
      };

      startedAt = now();
      poorMs = 0;
      lostMs = 0;
      reconnects = 0;
      playbackBlocked = false;
      spoke = false;
      micDenied = false;
      disconnectReason = null;
      reportedQuality = currentQuality;

      return snapshot;
    },
    // Короткий заход обычно пуст — но не тогда, когда микрофон не дали: человек видит, что его
    // не слышат, и выходит за секунду. Порог отсекал ровно эти сеансы, то есть самую интересную
    // часть отказов, и цифра «в приложении отказов нет» получалась из выживших.
    hasData: () => micDenied || now() - startedAt >= 3000,
  };
};
